import { useEffect, useMemo, useRef, useState } from "react";

import { poseOffsets, poseSrc, snap, type Axis } from "./figure-interaction";

/**
 * Every pose of a turnable figure, stacked, and only one of them on top.
 *
 * The obvious implementation — one `<img>` whose `src` changes as you drag —
 * flickers, and it is not the network: it flickers with everything cached. An SVG
 * image that has never been displayed has never been *rasterised*, so the first
 * frame it becomes visible is spent rasterising it. That is a blank frame per
 * pose, which during a turn is nearly every frame.
 *
 * So all the poses are in the DOM, each is painted once before the first gesture
 * (`warm`), and turning changes nothing but which is on top. They are opaque —
 * a figure's own background is transparent, so without that the ones underneath
 * would show through.
 *
 * The cost is one request per pose. They are a few KB of SVG each, cached for a
 * year and shared by every learner who opens that figure, and nothing is fetched
 * until somebody opens one full screen.
 */
export function FigurePoses({
  src,
  alt,
  axis,
  offset,
  canvas,
  className,
  style,
  onReady,
}: {
  src: string;
  alt: string;
  axis: Axis;
  /** Degrees away from the pose the question drew. */
  offset: number;
  /** The square every pose is rendered onto, in SVG units. */
  canvas: number;
  className?: string;
  style?: React.CSSProperties;
  onReady?: () => void;
}) {
  const offsets = useMemo(poseOffsets, []);
  const hostRef = useRef<HTMLDivElement>(null);
  // Which pose is being shown *for the warm-up*, which walks all of them before
  // handing control back to `offset`.
  const [warming, setWarming] = useState<number | null>(offsets[0]);

  useEffect(() => {
    setWarming(offsets[0]);
    let cancelled = false;
    let i = 0;

    // Two frames per pose: one to put it on top, one to let it actually be
    // painted before moving on. Decoding is not rasterising, so `img.decode()`
    // is not enough on its own.
    function step() {
      if (cancelled) return;
      if (i >= offsets.length) {
        setWarming(null);
        onReady?.();
        return;
      }
      setWarming(offsets[i++]);
      requestAnimationFrame(() => requestAnimationFrame(step));
    }
    const started = requestAnimationFrame(step);
    return () => {
      cancelled = true;
      cancelAnimationFrame(started);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, axis, canvas]);

  const shown = warming ?? snap(offset);

  return (
    <div ref={hostRef} className={className} style={style}>
      {offsets.map((value) => (
        <img
          key={value}
          src={poseSrc(src, axis, value, canvas)}
          // Only the pose actually being read is named; the rest are the same
          // object from another side, and a screen reader announcing 49 of them
          // would be worse than silence.
          alt={value === 0 ? alt : ""}
          aria-hidden={value === 0 ? undefined : true}
          draggable={false}
          className={value === shown ? "figure-pose figure-pose--on" : "figure-pose"}
        />
      ))}
    </div>
  );
}
